/**
 * Contact form state — form fields, step, errors, options, open detail.
 */
import { computed, ref } from 'vue'
import type {
  ContactMeOpenDetail,
  ContactMeStep,
  ContactMeWidgetOptions,
  StepOneModel,
  StepTwoModel,
} from '../types'

export type FormState = {
  firstName: string
  lastName: string
  email: string
  country: string
  phone: string
  hasWhatsApp: boolean
  message: string
  acceptPrivacyPolicy: boolean
  acceptMarketing: boolean
}

const initialForm: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  country: '',
  phone: '',
  hasWhatsApp: false,
  message: '',
  acceptPrivacyPolicy: false,
  acceptMarketing: false,
}

export function useContactFormState(opts: { t: (key: string) => string }) {
  const { t } = opts
  const optsRef = ref<ContactMeWidgetOptions>({})
  const openDetail = ref<ContactMeOpenDetail | undefined>(undefined)
  const isOpen = ref(false)
  const step = ref<ContactMeStep>(1)
  const isBusy = ref(false)
  const isShaking = ref(false)
  const errors = ref<Partial<Record<keyof FormState, string>>>({})
  const form = ref<FormState>({ ...initialForm })

  const stepOneModel = computed<StepOneModel>(() => ({
    email: form.value.email,
    acceptPrivacyPolicy: form.value.acceptPrivacyPolicy,
    acceptMarketing: form.value.acceptMarketing,
  }))

  const stepTwoModel = computed<StepTwoModel>(() => ({
    firstName: form.value.firstName,
    lastName: form.value.lastName,
    country: form.value.country,
    phone: form.value.phone,
    hasWhatsApp: form.value.hasWhatsApp,
    message: form.value.message,
  }))

  const brandTitle = computed(() => optsRef.value.brandName ?? t('contactForm.headerTitle'))
  const headerTitle = computed(() =>
    step.value === 3 ? t('contactForm.headerTitleSuccess') : t('contactForm.headerTitle')
  )
  const headerSubtitle = computed(() => {
    if (step.value === 1) return t('contactForm.headerSubtitleStep1')
    if (step.value === 2) return t('contactForm.headerSubtitleStep2')
    return t('contactForm.headerSubtitleSuccess')
  })

  function setError(key: keyof FormState, msg: string) {
    errors.value = { ...errors.value, [key]: msg }
  }

  function clearError(key: keyof FormState) {
    if (!errors.value[key]) return
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- rest destructuring to omit key
    const { [key]: _omit, ...rest } = errors.value
    errors.value = rest
  }

  function updateForm(partial: Partial<FormState>) {
    form.value = { ...form.value, ...partial }
    Object.keys(partial).forEach(k => clearError(k as keyof FormState))
  }

  function reset() {
    openDetail.value = undefined
    isBusy.value = false
    step.value = 1
    errors.value = {}
    form.value = { ...initialForm }
  }

  function applyPrefill(prefill?: ContactMeOpenDetail['prefill']) {
    if (!prefill) return
    if (typeof prefill.email === 'string') form.value.email = prefill.email
    if (typeof prefill.firstName === 'string') form.value.firstName = prefill.firstName
    if (typeof prefill.lastName === 'string') form.value.lastName = prefill.lastName
    if (typeof prefill.country === 'string') form.value.country = prefill.country.toUpperCase()
    if (typeof prefill.phone === 'string') form.value.phone = prefill.phone
    if (typeof prefill.message === 'string') form.value.message = prefill.message
    if (typeof prefill.hasWhatsApp === 'boolean') form.value.hasWhatsApp = prefill.hasWhatsApp
    if (typeof prefill.acceptPrivacyPolicy === 'boolean')
      form.value.acceptPrivacyPolicy = prefill.acceptPrivacyPolicy
    if (typeof prefill.acceptMarketing === 'boolean')
      form.value.acceptMarketing = prefill.acceptMarketing
  }

  function shake() {
    isShaking.value = false
    void document.body.offsetHeight
    isShaking.value = true
    window.setTimeout(() => (isShaking.value = false), 650)
  }

  return {
    optsRef,
    openDetail,
    isOpen,
    step,
    isBusy,
    isShaking,
    errors,
    form,
    stepOneModel,
    stepTwoModel,
    brandTitle,
    headerTitle,
    headerSubtitle,
    setError,
    clearError,
    updateForm,
    reset,
    applyPrefill,
    shake,
  }
}
